import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { FrequentlyAskedQuestion } from '@/components/HowToWin';
import { getForYouUrl } from '@/utils/urls';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { memo, useCallback } from 'react';
import Badges from './Badges';
import CompleteAccountBanner from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import FormattedMessageText from '@/components/Formatted/FormattedMessageText';
import HowAuctionsWorkPageHeaderMeta from './HowAuctionsWorkPageHeaderMeta';
import styled from 'styled-components';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';

const HowAuctionsWorkPage = () => {
    const mapQi = useCallback(
        (id) => (
            <FrequentlyAskedQuestion
                id={id}
                key={id}
            />
        ),
        []
    );

    return (
        <HowAuctionsWorkPageContainer>
            <HowAuctionsWorkPageHeaderMeta />
            <CompleteAccountBanner />
            <StepContainer>
                <StepSection>
                    <LeftSection>
                        <Title>
                            <Text
                                data-testid="howAuctionsWorkTitle"
                                h1
                            >
                                <FormattedMessage id="howAuctionsWorkPage.title" />
                            </Text>
                        </Title>
                        <FormattedMessageText
                            id="howAuctionsWorkPage.subtitle"
                            values={iOSAndroid}
                        />
                    </LeftSection>
                    <RightSection>
                        <ShowOnMobileImage
                            alt="step 1"
                            src="https://p1.liveauctioneers.com/dist/images/howauctionswork-laptop-1.jpg?width=560&quality=100"
                        />
                    </RightSection>
                </StepSection>
                <StepSection>
                    <LeftSection>
                        <HideOnMobileImage
                            alt="step 1"
                            src="https://p1.liveauctioneers.com/dist/images/438-4380469_iphone-xs-mockup-png-nobg.jpg?width=246&quality=100"
                        />
                    </LeftSection>
                    <RightSection>
                        <Title>
                            <Text h2>
                                <FormattedMessage id="step_one" />
                            </Text>
                        </Title>
                        <FormattedMessageText
                            encapsulateP
                            id="step_one_blurb"
                        />
                        <HR />
                        <Title>
                            <Text h2>
                                <FormattedMessage id="step_two" />
                            </Text>
                        </Title>
                        <FormattedMessageText
                            encapsulateP
                            id="step_two_blurb"
                            values={{
                                ...iOSAndroid,
                                dashboard: (
                                    <Link
                                        href={getForYouUrl()}
                                        key="how-auctions-work-dashboard-link"
                                        size="sm"
                                    >
                                        <FormattedMessage id="step_two_dashboard" />
                                    </Link>
                                ),
                            }}
                        />
                        <HR />
                    </RightSection>
                </StepSection>
                <StepSection>
                    <LeftSection>
                        <HideOnMobileImage
                            alt="step 1"
                            src="https://p1.liveauctioneers.com/dist/images/Screen-Shot-2020-04-09-at-1.58.43-PM-1.jpg?width=564&quality=100"
                        />
                    </LeftSection>
                    <RightSection>
                        <Title>
                            <Text h2>
                                <FormattedMessage id="step_three" />
                            </Text>
                        </Title>
                        <FormattedMessageText
                            encapsulateP
                            id="step_three_blurb"
                        />
                        <HR />
                    </RightSection>
                </StepSection>
                <StepSection>
                    <LeftSection>
                        <Title>
                            <Text h2>
                                <FormattedMessage id="step_four" />
                            </Text>
                        </Title>
                        <FormattedMessageText
                            id="step_four_blurb"
                            inline
                            values={iOSAndroid}
                        />
                        <HR />
                    </LeftSection>
                    <RightSection>
                        <HideOnMobileImage
                            alt="step 1"
                            src="https://p1.liveauctioneers.com/dist/images/Screen-Shot-2020-04-09-at-2.01.15-PM-1.jpg?width=691&quality=100"
                        />
                    </RightSection>
                </StepSection>
                <StepSection>
                    <LeftSection>
                        <HideOnMobileImage
                            alt="step 1"
                            src="https://p1.liveauctioneers.com/dist/images/81730951_1_x.jpg?width=564&quality=100"
                        />
                    </LeftSection>
                    <RightSection>
                        <Title>
                            <Text h2>
                                <FormattedMessage id="step_five" />
                            </Text>
                        </Title>
                        <FormattedMessageText
                            encapsulateP
                            id="step_five_blurb"
                        />
                        <HR />
                    </RightSection>
                </StepSection>
                <HideOnMobileHR />
                <StepSection>
                    <LeftSection>
                        <ShowOnMobileImage
                            alt="step 1"
                            src="https://p1.liveauctioneers.com/dist/images/mobile-app2.jpg?width=508&quality=100"
                        />
                    </LeftSection>
                    <RightSectionVertical>
                        <Title>
                            <Text h2>
                                <FormattedMessage id="step_app" />
                            </Text>
                        </Title>
                        <StyledBadgesContainer>
                            <Badges />
                        </StyledBadgesContainer>
                    </RightSectionVertical>
                </StepSection>
            </StepContainer>
            <QAContainer className="bg-background-subdued">
                <QALeft>
                    <Title data-testid="howAuctionsWorkFAQ">
                        <Text h2>
                            <FormattedMessage id="step_faq" />
                        </Text>
                    </Title>
                    <FormattedMessageText
                        encapsulateP
                        id="step_faq_descr"
                        values={{
                            knowledgeBase: (
                                <Link
                                    className="!no-underline"
                                    href="https://help.liveauctioneers.com/"
                                    key="how-auctions-work-knowledge-base-link"
                                    size="sm"
                                >
                                    knowledge base
                                </Link>
                            ),
                        }}
                    />
                </QALeft>
                <QARight>{QAIds.map(mapQi)}</QARight>
            </QAContainer>
        </HowAuctionsWorkPageContainer>
    );
};

// because baseUrl will never update we can keep memo
export default memo(HowAuctionsWorkPage);

const HowAuctionsWorkPageContainer = styled.div`
    @media (max-width: 800px) {
        padding-top: 20px;
    }
`;
const StepContainer = styled.div`
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 0 auto;
    padding: 0 20px 30px;
`;
const StepSection = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    margin-top: 30px;

    @media (max-width: 800px) {
        flex-direction: column;
        margin: 0 auto;
        justify-content: center;
    }
`;
const LeftSection = styled.div`
    @media (min-width: 801px) {
        float: left;
        padding-right: 20px;
    }
`;
const RightSection = styled.div`
    @media (min-width: 801px) {
        float: right;
        padding-left: 20px;
    }
`;
const RightSectionVertical = styled(RightSection)`
    display: flex;
    flex-direction: column;
    align-items: center;
`;
const Title = styled.div`
    padding-bottom: 18px;
`;
const HideOnMobileImage = styled.img`
    @media (max-width: 800px) {
        display: none;
    }
`;
const ShowOnMobileImage = styled.img`
    @media (max-width: 800px) {
        width: calc(100% - 40px);
        margin: 0 auto;
        padding-top: 10px;
        padding-bottom: 15px;
    }
`;
const QAContainer = styled.div`
    width: 100%;
    padding: 40px 20px;
    display: flex;
    justify-content: center;

    @media (max-width: 800px) {
        flex-direction: column;
    }
`;
const QALeft = styled.div`
    flex-grow: 1;
    width: 100%;

    @media (max-width: 800px) {
        & > p:last-child {
            margin-bottom: 15px;
        }
    }

    @media (min-width: 801px) {
        max-width: 353px;
    }
`;
const QARight = styled.div`
    flex-grow: 2;
    width: 100%;

    @media (min-width: 801px) {
        max-width: 807px;
        margin-left: 80px;
    }
`;

const StyledBadgesContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
`;

const HR = styled.div`
    width: 100%;
    background-color: ${({ theme }) => theme.colors.grey400};
    margin: 37px 0;
    display: block;
    height: 1px;
`;
const HideOnMobileHR = styled(HR)`
    @media (max-width: 800px) {
        display: none;
    }
`;

const AndroidLink = ({ children }) => (
    <Link
        href="https://play.google.com/store/apps/details?id=com.liveauctioneers.and"
        size="sm"
    >
        {children}
    </Link>
);

const IOSLink = ({ children }) => (
    <Link
        href="https://apps.apple.com/us/app/live-auctions/id321243082"
        size="sm"
    >
        {children}
    </Link>
);

const iOSAndroid = {
    Android: <AndroidLink>Android</AndroidLink>,
    iOS: <IOSLink>iOS</IOSLink>,
};

const QAIds = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
